import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import UserService from 'services/user.service';
import { settingActiveOrgRequest } from 'store/sagas/Org';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import {
    APP_CONTEXT,
    canSwitchApp,
    getAppContext,
    getSwitchTarget
} from 'utils/app-context';

const LABELS = {
    [APP_CONTEXT.RESI]: 'Switch to Resident app',
    [APP_CONTEXT.OPERATOR]: 'Switch to Operator app'
};

// Switching between the resident and the operator app for someone who is both a team member
// and a resident. It only moves the user's active org and property to the other side; which app
// is shown follows from them (see `utils/app-context.js`). A full reload then starts the
// listeners and routes of the app they land in.
export const useSwitchApp = () => {
    const userData = useSelector(state => state.user.userData);
    const [switching, setSwitching] = useState(false);

    const target = canSwitchApp(userData)
        ? getAppContext(userData) === APP_CONTEXT.OPERATOR
            ? APP_CONTEXT.RESI
            : APP_CONTEXT.OPERATOR
        : null;

    const switchApp = useCallback(async () => {
        const destination = target && getSwitchTarget(userData, target);

        if (!destination || switching) return;

        setSwitching(true);

        try {
            if (destination.propertyId) {
                // Also sets the active org to the property's org.
                await UserService.setActiveProperty(userData.uid, destination.propertyId);
            } else {
                await settingActiveOrgRequest({
                    active_org_id: destination.orgId,
                    active_station_id: userData.active_station_id,
                    userId: userData.uid
                });
            }

            window.location.assign('/');
        } catch (error) {
            console.error('Error switching app:', error);
            SnackbarNotify.error('Could not switch the app. Please try again.');
            setSwitching(false);
        }
    }, [target, userData, switching]);

    return {
        canSwitch: Boolean(target),
        label: target ? LABELS[target] : '',
        switching,
        switchApp
    };
};
